import React, { useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import { GoogleLogin } from "@react-oauth/google";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../services/api";
import AppleSignInButton from "./AppleSignInButton";

function postToNative(message) {
  if (typeof window === "undefined") return false;
  if (!window.ReactNativeWebView?.postMessage) return false;
  window.ReactNativeWebView.postMessage(JSON.stringify(message));
  return true;
}

export default function MobileSocialAuthBridge() {
  const { t } = useTranslation();
  const { provider } = useParams();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState({
    apple: false,
    google: false,
    facebook: false,
    apple_client_id: "",
    apple_redirect_uri: "",
  });

  const normalizedProvider = useMemo(
    () => String(provider || "").trim().toLowerCase(),
    [provider]
  );

  const desiredPlan = useMemo(() => {
    const query = new URLSearchParams(window.location.search || "");
    return (query.get("desired_plan") || "free").trim().toLowerCase() || "free";
  }, []);

  const facebookRedirectUri = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}${window.location.pathname}`;
  }, []);

  React.useEffect(() => {
    let active = true;
    api.users
      .getAuthProviders()
      .then((response) => {
        if (!active) return;
        const payload = response?.data || {};
        setProviders({
          apple: Boolean(payload.apple),
          google: Boolean(payload.google),
          facebook: Boolean(payload.facebook),
          apple_client_id: String(payload.apple_client_id || "").trim(),
          apple_redirect_uri: String(payload.apple_redirect_uri || "").trim(),
        });
      })
      .catch(() => {
        if (!active) return;
        setProviders({
          apple: false,
          google: false,
          facebook: false,
          apple_client_id: "",
          apple_redirect_uri: "",
        });
      });
    return () => {
      active = false;
    };
  }, []);

  React.useEffect(() => {
    if (normalizedProvider !== "facebook" || typeof window === "undefined") {
      return;
    }

    const params = new URLSearchParams(window.location.search || "");
    const code = params.get("code");
    const state = params.get("state");
    if (!code || !state || !facebookRedirectUri) {
      return;
    }

    void handleSuccess(
      api.users.facebookCodeLogin({
        code,
        state,
        redirect_uri: facebookRedirectUri,
        desired_plan: desiredPlan,
      })
    );
  }, [desiredPlan, facebookRedirectUri, normalizedProvider]);

  const startFacebookLogin = () => {
    if (!facebookRedirectUri || typeof window === "undefined") {
      setError("Facebook Sign-In is unavailable right now.");
      return;
    }
    setLoading(true);
    setError("");
    const apiBase = String(process.env.REACT_APP_USERS_ROUTES_API_BASE_URL || "").replace(/\/+$/, "");
    if (!apiBase) {
      setLoading(false);
      setError("Facebook Sign-In is unavailable right now.");
      return;
    }
    const url = `${apiBase}/login/facebook/start?redirect_uri=${encodeURIComponent(facebookRedirectUri)}`;
    window.location.assign(url);
  };

  const handleSuccess = async (payloadPromise) => {
    setLoading(true);
    setError("");
    try {
      const payload = await payloadPromise;
      if (!postToNative({ type: "auth-success", payload: payload?.data || payload })) {
        setError("This auth bridge must be opened from the mobile app.");
      }
    } catch (nextError) {
      const message =
        nextError?.response?.data?.error ||
        nextError?.message ||
        "Authentication failed.";
      setError(message);
      postToNative({ type: "auth-error", error: message });
    } finally {
      setLoading(false);
    }
  };

  const renderProvider = () => {
    if (normalizedProvider === "google") {
      if (!providers.google) {
        return <Alert severity="error">{t("auth_provider_unavailable")}</Alert>;
      }
      return (
        <Box
          sx={{
            width: "100%",
            "& iframe": { width: "100% !important" },
            "& > div": { width: "100% !important" },
            "& > div > div": { width: "100% !important" },
          }}
        >
          <GoogleLogin
            onSuccess={(credentialResponse) =>
              handleSuccess(
                api.users.googleLogin({
                  token: credentialResponse.credential,
                  desired_plan: desiredPlan,
                })
              )
            }
            onError={() => setError(t("google_sign_in_failed"))}
            useOneTap={false}
          />
        </Box>
      );
    }

    if (normalizedProvider === "apple") {
      if (!providers.apple) {
        return <Alert severity="error">{t("auth_provider_unavailable")}</Alert>;
      }
      return (
        <AppleSignInButton
          label={t("login_with_apple")}
          onSuccess={({ idToken, user }) =>
            handleSuccess(
              api.users.appleLogin({
                id_token: idToken,
                desired_plan: desiredPlan,
                user,
                email: user?.email || null,
                is_private_email: false,
              })
            )
          }
          onError={(nextError) =>
            setError(nextError?.message || t("apple_sign_in_failed"))
          }
          clientId={providers.apple_client_id}
          redirectURI={providers.apple_redirect_uri}
        />
      );
    }

    if (normalizedProvider === "facebook") {
      if (!providers.facebook) {
        return <Alert severity="error">{t("auth_provider_unavailable")}</Alert>;
      }
      return (
        <Button
          variant="contained"
          startIcon={<FacebookIcon />}
          onClick={startFacebookLogin}
          disabled={loading}
          sx={{
            justifyContent: "center",
            minHeight: 44,
            borderRadius: 2.5,
            fontWeight: 700,
            bgcolor: "#4267B2",
            "&:hover": { bgcolor: "#37508b" },
          }}
        >
          Continue with Facebook
        </Button>
      );
    }

    return <Alert severity="error">Unsupported auth provider.</Alert>;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        background:
          "radial-gradient(circle at top, rgba(73,188,144,0.14), transparent 40%), #fbfaf7",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 420,
          borderRadius: 5,
          p: 4,
          border: "1px solid rgba(18,25,20,0.08)",
          boxShadow: "0 20px 60px rgba(18,25,20,0.08)",
        }}
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="overline" sx={{ letterSpacing: 1.8, color: "text.secondary" }}>
              InCooking Mobile
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>
              Continue securely
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Use the same InCooking account across web and mobile.
            </Typography>
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}
          {renderProvider()}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
              <CircularProgress size={22} />
            </Box>
          ) : null}
        </Stack>
      </Paper>
    </Box>
  );
}
